import { calculateFinalPositions } from "model/Positions";
import { mapMatchmakingFormat } from "./FsMatchmakingFormat";
import { mapMatchmakingTracks } from "./FsMatchmakingTrack";
import { nonUndefined } from "Utility";
import { MatchmakingSet } from "model/Match/Set/Matchmaking/MatchmakingSet";
import { MatchTeams } from "model/Match/MatchTeams";
import { MatchmakingTrack } from "model/Match/Set/Matchmaking/Track/MatchmakingTrack";
import { Scores } from "model/Scores";
import { FsMatchmakingSet } from "db/model/match/Set/Matchmaking/FsMatchmakingSet";
import {
  calculateIsCompletePlayCondition,
  mapMatchSetBase,
} from "../FsMatchSet";
import { getResults } from "model/Results";
import { getDefinedRosterIds } from "model/Roster/Roster";

const NUM_WINNERS = 1;

export const mapMatchmakingSet = (
  fsModel: FsMatchmakingSet,
  teams: MatchTeams,
  isSms: boolean
): MatchmakingSet | undefined => {
  if (!fsModel.format || !fsModel.format.trackWinReq) {
    return undefined;
  }

  const format = mapMatchmakingFormat(fsModel.format);
  if (!format) {
    return undefined;
  }

  const tracks = mapMatchmakingTracks(
    nonUndefined(fsModel.tracks ?? []),
    teams.rosters,
    teams.scope,
    format.points,
    fsModel.format.roundsLimit,
    fsModel.format.trackWinReq,
    fsModel.overrideComplete,
    isSms
  );

  const scores = calculateSetScores(
    tracks,
    fsModel.scoreOverrides,
    getDefinedRosterIds(teams.rosters)
  );

  const numCompleteTracks = tracks.filter((t) => t.isComplete).length;
  const isComplete = calculateIsCompletePlayCondition(
    scores,
    format.numTracks,
    numCompleteTracks,
    format.playCondition
  );

  const finalPositions = calculateFinalPositions(
    isComplete,
    scores,
    compareScores
  );

  return {
    ...mapMatchSetBase(fsModel, isComplete, finalPositions, scores),
    format,
    tracks,
    type: "Matchmaking",
  };
};

// Assumes each track awards winner 1 point
// Assumes draw = 0 points for each team
const calculateSetScores = (
  tracks: MatchmakingTrack[],
  scoreOverrides: Scores | undefined,
  teamIds: string[]
) => {
  const scores: Scores = {};
  const trackResults = nonUndefined(
    tracks.map((t) => getResults(t.finalPositions, NUM_WINNERS))
  );
  for (var i = 0; i < teamIds.length; i++) {
    const teamId = teamIds[i];
    const scoreOverride = !!scoreOverrides ? scoreOverrides[teamId] : undefined;
    if (scoreOverride !== undefined) {
      scores[teamId] = scoreOverride;
    } else {
      scores[teamId] = trackResults.filter(
        (results) => results[teamId] === "W"
      ).length;
    }
  }

  return scores;
};

// todo - Add tiebreaker logic here
const compareScores = (scores: Scores, a: string, b: string) => {
  return scores[b] - scores[a];
};
